import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';
import LocationModal from 'components/modals/LocationModal';
import { locationColumns } from 'components/tables/LocationTable';
import { GET_LOCATION_QUERY } from 'services/api/location';
import { useQuery } from 'react-query';
import { useState, ChangeEvent, useMemo } from 'react';
import { Col, Row, Placeholder, Alert } from 'react-bootstrap';
import GTable from 'components/tables/GTable';
import { PERMISSIONS } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';

const Location = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [openLocationModel, setOpenLocationModel] = useState(false);
  const {
    isLoading,
    isError,
    data: location
  } = useQuery('get_all_location', GET_LOCATION_QUERY, {
    staleTime: 3 * 60 * 60 * 1000
  });
  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const [canCreateLocation, canEditLocation, canDeleteLocation] =
    usePermissions([
      PERMISSIONS.JOB_CONFIGURATION.JOB_LOCATION.CREATE,
      PERMISSIONS.JOB_CONFIGURATION.JOB_LOCATION.UPDATE,
      PERMISSIONS.JOB_CONFIGURATION.JOB_LOCATION.DELETE
    ]);

  const filteredLocations =
    location?.data
      ?.sort((a: any, b: any) => {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      })
      .filter((loc: any) =>
        loc.location?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a: any, b: any) => {
        return a?.location?.localeCompare(b?.location ?? '');
      }) ?? [];

  const columns = useMemo(
    () => locationColumns(canEditLocation, canDeleteLocation),
    [canEditLocation, canDeleteLocation]
  );

  return (
    <div>
      <div className="mb-6">
        {isError && <Alert variant="danger">Error loading job locations</Alert>}
        <h2 className="mb-4">All Job Location</h2>
        <Row className="g-3 justify-content-between mb-4">
          {canCreateLocation && (
            <Col xs="auto">
              <Button
                variant="primary"
                className="me-4"
                startIcon={<FontAwesomeIcon icon={faPlus} className="me-2" />}
                onClick={() => setOpenLocationModel(true)}
              >
                Create New Location
              </Button>
              <LocationModal
                show={openLocationModel}
                handleClose={() => setOpenLocationModel(false)}
              />
            </Col>
          )}
          <Col xs="auto">
            <div className="d-flex">
              <SearchBox
                placeholder="Search by location"
                className="me-2"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
            </div>
          </Col>
        </Row>
        <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
          {isLoading ? (
            <div className="g-3 mt-8 mb-8">
              <Placeholder as="p" animation="glow">
                <Placeholder xs={12} />
              </Placeholder>
              <Placeholder as="p" animation="glow">
                <Placeholder xs={12} />
              </Placeholder>
            </div>
          ) : (
            <GTable
              columns={columns}
              data={filteredLocations}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Location;
